import { inject } from '@angular/core';
import { EnvironmentInstanceToken } from '@evorait/pace-environment';
import { Store } from '@ngrx/store';
import { merge, Observable } from 'rxjs';
import { filter, map, skip, withLatestFrom } from 'rxjs/operators';
import { NotificationCompletedListModel } from '../../models/low-level/notification-completed.model';
import { EventService } from '../../services/event/event.service';
import { isDefined } from '../../utilities/is-defined';
import { selectDisplayServicePartQuestionnaire } from '../config/selectors/config.selector';
import { completedNotificationsToListModel } from './notification-collection.converters';
import {
    selectCompletedNotificationEntries,
    selectNotificationsCancelledSequence,
    selectNotificationsLoadedSequence,
    selectNotificationsUpdatedSequence,
} from './selectors/notification.selector';

/**
 * The streams below are how the app hears that the notification list changed. Each returns a
 * store-backed stream once the notification domain is served by the slice, and the matching
 * {@link EventService} stream while it is not: without the slice, the service behind
 * `NotificationDataInstanceToken` loads the notifications and announces its loads as events.
 *
 * The three that report a change read the slice's markers (see `NotificationCollectionState`'s
 * `lastLoad` and `lastCancellation`) rather than the entry collection itself: a reader wants to
 * know that a load completed, not that some entry changed. `skip(1)` drops the value every
 * `store.select` replays on subscribe, so those behave like the subjects they replace — a
 * subscriber hears about the loads that happen after it subscribed, not about the last one before.
 * {@link completedNotifications$} is the exception: it reports state, not a change, so it keeps
 * the replay.
 *
 * All of them must be called from an injection context. Delete them, and keep only what their
 * store branches do, once `enableNgRx` is permanent.
 */

/**
 * Whether the NgRx slices serve this app's data — the same flag `app.module.ts` picks every
 * `*InstanceToken` implementation from. Injected optionally: the standalone forms apps use parts of this
 * library without providing a `PaceEnvironment`, and for them the answer is "no".
 */
export function isNgRxEnabled(): boolean {
    return inject(EnvironmentInstanceToken, { optional: true })?.enableNgRx === true;
}

/**
 * Ticks whenever the notification list changed in a way its readers have to act on: a completed
 * reload — the app-start one excluded, since on the first load there is nothing to react to yet — or
 * a confirmed cancellation, which is what an emitted `true` means.
 *
 * Both halves come from the slice: the cancellation dialog announces itself by dispatching
 * `notificationCancelled`, which is what `lastCancellation` counts. Unlike the `EventService`
 * cancellation event it replaces, the store emission is synchronous with that dispatch rather than
 * deferred by a `setTimeout` — the same way the reload half already emits.
 */
export function notificationsUpdated$(): Observable<boolean | undefined> {
    if (!isNgRxEnabled()) {
        return inject(EventService).notificationsUpdated$;
    }
    const store = inject(Store);
    return merge(
        store.select(selectNotificationsCancelledSequence).pipe(
            skip(1),
            filter(isDefined),
            map(() => true),
        ),
        store.select(selectNotificationsUpdatedSequence).pipe(
            skip(1),
            filter(isDefined),
            map(() => undefined),
        ),
    );
}

/**
 * Ticks after every completed reload of the notification list, the app-start one included: the
 * store-backed counterpart of `EventService.triggerUpdateNotificationCountOnSuccessfulSync$`.
 */
export function notificationsReloaded$(): Observable<void> {
    if (!isNgRxEnabled()) {
        return inject(EventService).triggerUpdateNotificationCountOnSuccessfulSync$;
    }
    return inject(Store)
        .select(selectNotificationsLoadedSequence)
        .pipe(
            skip(1),
            filter(isDefined),
            map(() => undefined),
        );
}

/**
 * Ticks after every completed reload of the notification list while the service-part questionnaire
 * feature is configured on: the store-backed counterpart of `EventService.triggerSPQDocRefresh$`.
 * The slice announces every reload unconditionally, so the feature check has to happen here, on the
 * consuming side, rather than where the reload is announced.
 */
export function spqDocRefreshRequired$(): Observable<void> {
    if (!isNgRxEnabled()) {
        return inject(EventService).triggerSPQDocRefresh$;
    }
    const store = inject(Store);
    return store.select(selectNotificationsLoadedSequence).pipe(
        skip(1),
        filter(isDefined),
        withLatestFrom(store.select(selectDisplayServicePartQuestionnaire)),
        filter(([, displayServicePartQuestionnaire]) => displayServicePartQuestionnaire),
        map(() => undefined),
    );
}

/**
 * The notifications whose confirmation was completed — the ones this device completed and the ones
 * a completion recorded on another device brought in. State rather than an event: the store branch
 * replays the current list on subscribe, exactly like the `BehaviorSubject` behind
 * `EventService.completedNotifications$` does, so both branches let a late subscriber see what is
 * already completed instead of waiting for the next change.
 */
export function completedNotifications$(): Observable<NotificationCompletedListModel> {
    if (!isNgRxEnabled()) {
        return inject(EventService).completedNotifications$;
    }
    return inject(Store).select(selectCompletedNotificationEntries).pipe(map(completedNotificationsToListModel));
}
